/**
 * The placeholder pages behind the Solutions / Development / Administration
 * roadmap entries in the sidebar.
 *
 * Two kinds share one component:
 *
 * - **Being built** — the Base solutions not yet live (Explore is live at
 *   `/solutions/explore`, todo-demo-08).
 *   Their routes are live and final; the real page replaces the
 *   placeholder in a later change without moving the URL.
 * - **Coming soon** — the Validation and Clinical roadmap items and
 *   Documentation. The nav marks these with a "Soon" badge.
 *
 * Inert by design (ADR-0004): no API calls beyond `/me`, no data handling. The
 * `requiredRoles` shown are informational; the route guard and the backend are
 * what enforce access.
 */

import { ModulePlaceholder } from "./ModulePlaceholder";

const ALL_ROLES = ["user", "developer", "administrator"] as const;
const DEVELOPER_PLUS = ["developer", "administrator"] as const;

interface PlaceholderSpec {
  name: string;
  tagline: string;
  phase: string;
  requiredRoles: readonly string[];
}

/** Keyed by a stable slug; the router picks the entry, the copy lives here. */
export const SOLUTION_PLACEHOLDERS = {
  create: {
    name: "Create",
    tagline: "Produce new material from your documents: translation, and question-and-answer.",
    phase: "Being built",
    requiredRoles: ALL_ROLES,
  },
  "my-tests": {
    name: "My tests",
    tagline: "Your validation tests, in one place.",
    phase: "Coming soon",
    requiredRoles: ALL_ROLES,
  },
  documentation: {
    name: "Documentation",
    tagline: "Qualification and release reports for the platform.",
    phase: "Coming soon",
    requiredRoles: DEVELOPER_PLUS,
  },
} satisfies Record<string, PlaceholderSpec>;

export type SolutionPlaceholderSlug = keyof typeof SOLUTION_PLACEHOLDERS;

export function SolutionPlaceholderPage({ slug }: { slug: SolutionPlaceholderSlug }) {
  const spec: PlaceholderSpec = SOLUTION_PLACEHOLDERS[slug];
  return (
    <ModulePlaceholder
      name={spec.name}
      tagline={spec.tagline}
      phase={spec.phase}
      requiredRoles={spec.requiredRoles}
    />
  );
}
